import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Bookmark, Calculator as CalcIcon, User, LogOut, ArrowRight, ArrowLeft, Search, Star } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CALENDLY_URL } from "../mock";
import { dealsApi, savedApi, meApi } from "../api";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const money = (n) => "$" + Number(n || 0).toLocaleString();

function MiniCalc() {
  const [price, setPrice] = useState(300000);
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(7.5);
  const [rent, setRent] = useState(2400);
  const [ti, setTi] = useState(280);
  const loan = price * (1 - down / 100);
  const mr = rate / 100 / 12;
  const pi = mr > 0 ? (loan * mr * Math.pow(1 + mr, 360)) / (Math.pow(1 + mr, 360) - 1) : loan / 360;
  const pitia = pi + Number(ti);
  const dscr = pitia > 0 ? rent / pitia : 0;
  const cf = rent - pitia;
  const q = dscr >= 1;
  return (
    <div className="card" style={{ padding: "2rem" }}>
      <h3 className="font-serif" style={{ fontSize: "1.5rem", marginBottom: "0.4rem" }}>DSCR Calculator</h3>
      <p className="body-text" style={{ fontSize: "0.85rem", marginBottom: "1.6rem" }}>Estimate DSCR ratio and cash flow for any property.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
        <div style={{ display: "grid", gap: "1rem" }}>
          {[["Purchase Price ($)", price, setPrice], ["Down Payment (%)", down, setDown], ["Interest Rate (%)", rate, setRate], ["Monthly Rent ($)", rent, setRent], ["Tax + Insurance ($/mo)", ti, setTi]].map(([l, v, s]) => (
            <div key={l}>
              <label style={{ display: "block", fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "0.4rem" }}>{l}</label>
              <input className="field" type="number" value={v} onChange={(e) => s(Number(e.target.value))} />
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gap: "0.8rem", alignContent: "start" }}>
          {[["Loan Amount", "$" + Math.round(loan).toLocaleString()], ["Monthly P&I", "$" + Math.round(pi).toLocaleString()], ["Total PITIA", "$" + Math.round(pitia).toLocaleString()]].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", paddingBottom: "0.6rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ color: "var(--muted)", fontSize: "0.85rem" }}>{l}</span>
              <span className="font-serif" style={{ fontSize: "1.1rem" }}>{v}</span>
            </div>
          ))}
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <div className="eyebrow" style={{ color: "var(--muted-2)" }}>DSCR Ratio</div>
            <div className="font-serif" style={{ fontSize: "3rem", color: q ? "var(--green)" : "var(--red)" }}>{dscr.toFixed(2)}</div>
            <div style={{ color: q ? "var(--green)" : "var(--red)", fontSize: "0.82rem" }}>{q ? "\u2713 Qualifies for DSCR Financing" : "Below 1.0"}</div>
            <div style={{ color: cf >= 0 ? "var(--green)" : "var(--red)", fontSize: "0.9rem", marginTop: "0.6rem" }}>Est. Cash Flow: {(cf >= 0 ? "+$" : "-$") + Math.abs(Math.round(cf)).toLocaleString()}</div>
          </div>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="btn-gold" style={{ width: "100%" }}>Get Real Loan Terms <ArrowRight size={14} /></a>
        </div>
      </div>
    </div>
  );
}

export default function UserDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("dashboard");
  const [deals, setDeals] = useState([]);
  const [savedList, setSavedList] = useState([]);
  const [summary, setSummary] = useState({ savedCount: 0, projectedCF: 0, inquiriesCount: 0, avgDscr: 0, recentActivity: [] });
  const [query, setQuery] = useState("");
  const [bMarket, setBMarket] = useState("All Markets");
  const [minDscr, setMinDscr] = useState(0);
  const name = user?.firstName || "Investor";
  const savedIds = savedList.map((d) => d.id);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  const loadSaved = () => savedApi.list().then(setSavedList).catch(() => setSavedList([]));
  const loadSummary = () => meApi.summary().then(setSummary).catch(() => {});

  useEffect(() => {
    dealsApi.list({ status: "LIVE", sort: "cash" }).then(setDeals).catch(() => setDeals([]));
    loadSaved();
    loadSummary();
  }, []);

  const toggleSave = async (id) => {
    if (savedIds.includes(id)) { await savedApi.unsave(id); toast("Removed from saved"); }
    else { await savedApi.save(id); toast.success("Saved"); }
    loadSaved();
    loadSummary();
  };

  const filtered = deals.filter((d) => {
    const q = query.trim().toLowerCase();
    const matchQ = !q || `${d.address} ${d.city} ${d.market}`.toLowerCase().includes(q);
    const matchM = bMarket === "All Markets" || d.market === bMarket;
    const matchD = (d.dscr || 0) >= Number(minDscr);
    return matchQ && matchM && matchD;
  });

  const nav = [
    ["dashboard", "Dashboard", LayoutDashboard],
    ["browse", "Browse Deals", Search],
    ["saved", "Saved Properties", Bookmark],
    ["calculator", "DSCR Calculator", CalcIcon],
    ["profile", "My Profile", User],
  ];

  const DealCard = ({ d }) => (
    <div className="card" style={{ padding: "1.5rem", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.8rem" }}>
        <span style={{ fontSize: "0.66rem", padding: "0.25rem 0.6rem", borderRadius: 3, background: "var(--gold-soft)", color: "var(--gold)" }}>{d.badge}</span>
        <button onClick={() => toggleSave(d.id)} style={{ background: "none", border: "none", cursor: "pointer", color: savedIds.includes(d.id) ? "var(--gold)" : "var(--muted-2)" }}>
          <Star size={18} fill={savedIds.includes(d.id) ? "var(--gold)" : "none"} />
        </button>
      </div>
      <h3 className="font-serif" style={{ fontSize: "1.25rem" }}>{d.address}</h3>
      <div style={{ color: "var(--muted)", fontSize: "0.82rem", marginBottom: "1rem" }}>{d.city} · {d.market}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "0.6rem", marginBottom: "0.6rem" }}>
        {[["$" + Number(d.price).toLocaleString(), "Price"], ["+$" + d.cashflow, "Cash Flow"], [d.dscr, "DSCR"]].map(([v, l], i) => (
          <div key={i}>
            <div className="font-serif" style={{ fontSize: "1.1rem", color: i === 1 ? "var(--green)" : i === 2 ? "var(--gold)" : "var(--text)" }}>{v}</div>
            <div style={{ fontSize: "0.58rem", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted-2)" }}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{ color: "var(--gold)", fontSize: "0.78rem" }}>Rent: ${Number(d.rent).toLocaleString()}/mo</div>
    </div>
  );

  return (
    <div className="dash-shell" style={{ display: "flex", minHeight: "100vh", background: "#0a0a0c" }}>
      <aside className="dash-aside" style={{ width: 240, borderRight: "1px solid rgba(255,255,255,0.06)", padding: "2rem 1.2rem", position: "sticky", top: 0, height: "100vh", display: "flex", flexDirection: "column" }}>
        <div className="font-serif" style={{ fontSize: "1.4rem", padding: "0 0.6rem", marginBottom: "2.5rem" }}>
          <span style={{ color: "var(--text)" }}>DSCR</span><span style={{ color: "var(--gold)" }}>Daily</span>
        </div>
        <nav style={{ display: "grid", gap: "0.3rem", flex: 1 }}>
          {nav.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setView(k)} style={{ display: "flex", alignItems: "center", gap: "0.8rem", padding: "0.8rem 0.9rem", borderRadius: 4, border: "none", cursor: "pointer", textAlign: "left", fontSize: "0.85rem", background: view === k ? "var(--gold-soft)" : "transparent", color: view === k ? "var(--gold)" : "var(--muted)", transition: "all 0.2s ease" }}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </nav>
        <button onClick={() => navigate("/")} className="btn-dark" style={{ marginBottom: "0.6rem", justifyContent: "flex-start" }}><ArrowLeft size={14} /> View Site</button>
        <button onClick={() => { logout(); navigate("/"); }} style={{ display: "flex", alignItems: "center", gap: "0.8rem", padding: "0.8rem 0.9rem", borderRadius: 4, border: "none", cursor: "pointer", background: "transparent", color: "var(--muted)", fontSize: "0.85rem" }}><LogOut size={16} /> Sign Out</button>
      </aside>

      <main style={{ flex: 1, padding: "2.5rem 3rem" }}>
        {view === "dashboard" && (
          <>
            <h1 className="font-serif" style={{ fontSize: "2.2rem", marginBottom: "2rem" }}>Good to see you, {name} <span style={{ WebkitTextFillColor: "initial" }}>&#128075;</span></h1>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: "1.2rem", marginBottom: "2.5rem" }}>
              {[
                [String(summary.savedCount), "Saved Properties"],
                [(summary.projectedCF >= 0 ? "+$" : "-$") + Math.abs(summary.projectedCF).toLocaleString(), "Projected Monthly CF"],
                [String(summary.inquiriesCount), "Active Inquiries"],
                [summary.avgDscr ? summary.avgDscr.toFixed(2) : "—", "Avg DSCR (Saved)"],
              ].map(([v, l], i) => (
                <div key={i} className="card" style={{ padding: "1.6rem" }} data-testid={`member-stat-${i}`}>
                  <div className="font-serif" style={{ fontSize: "2.2rem", color: i === 1 || i === 3 ? "var(--gold)" : "var(--text)" }}>{v}</div>
                  <div className="eyebrow" style={{ color: "var(--muted-2)", marginTop: "0.4rem" }}>{l}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "2.5rem" }}>
              <div className="card" style={{ padding: "2rem" }}>
                <h3 className="font-serif" style={{ fontSize: "1.4rem", marginBottom: "1.4rem" }}>Recent Activity</h3>
                {summary.recentActivity.length === 0 ? (
                  <p className="body-text" style={{ fontSize: "0.88rem" }}>No activity yet. Browse deals and save the ones you like, or request DSCR terms on a property.</p>
                ) : (
                  <div style={{ display: "grid", gap: "1rem" }}>
                    {summary.recentActivity.map((a, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "1rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <div>
                          <div style={{ fontSize: "0.88rem", color: "var(--text)" }}>{a.text}</div>
                          <div style={{ fontSize: "0.75rem", color: "var(--muted-2)" }}>{timeAgo(a.time)}</div>
                        </div>
                        <span style={{ fontSize: "0.68rem", padding: "0.25rem 0.7rem", borderRadius: 999, background: "var(--gold-soft)", color: "var(--gold)" }}>{a.tag}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="card" style={{ padding: "2rem" }}>
                <h3 className="font-serif" style={{ fontSize: "1.4rem", marginBottom: "1.4rem" }}>New Deals Matching Your Criteria</h3>
                <div style={{ display: "grid", gap: "0.9rem" }}>
                  {deals.slice(0, 3).map((d) => (
                    <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.9rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <div>
                        <div style={{ fontSize: "0.72rem", color: "var(--muted-2)" }}>{d.type.split(" · ")[0]} · {d.market}</div>
                        <div style={{ fontSize: "0.9rem", color: "var(--text)" }}>{d.address}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ color: "var(--green)", fontSize: "0.85rem" }}>+${d.cashflow}/mo</div>
                        <div style={{ color: "var(--gold)", fontSize: "0.75rem" }}>DSCR {d.dscr}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {view === "browse" && (
          <>
            <h1 className="font-serif" style={{ fontSize: "2rem", marginBottom: "0.4rem" }}>Browse Deals</h1>
            <p className="body-text" style={{ marginBottom: "1.6rem" }}>Search all live DSCR deals and save the ones you like.</p>
            <div style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap", marginBottom: "1.6rem" }}>
              <div style={{ position: "relative", flex: "1 1 240px" }}>
                <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--muted-2)" }} />
                <input className="field" style={{ paddingLeft: 36 }} placeholder="Search address, city, or market…" value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
              <select className="field" style={{ width: "auto" }} value={bMarket} onChange={(e) => setBMarket(e.target.value)}>
                {["All Markets", "Florida", "Texas", "Georgia", "Carolinas", "Midwest", "Maryland", "Virginia", "DC / DMV", "Alabama", "Missouri", "Kansas", "Illinois", "Tennessee", "Michigan", "New York", "Pennsylvania"].map((m) => <option key={m}>{m}</option>)}
              </select>
              <select className="field" style={{ width: "auto" }} value={minDscr} onChange={(e) => setMinDscr(e.target.value)}>
                <option value={0}>Any DSCR</option><option value={1}>DSCR ≥ 1.0</option><option value={1.2}>DSCR ≥ 1.2</option><option value={1.3}>DSCR ≥ 1.3</option>
              </select>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: "1.2rem" }}>
              {filtered.map((d) => <DealCard key={d.id} d={d} />)}
              {filtered.length === 0 && <p className="body-text">No deals match your search.</p>}
            </div>
          </>
        )}

        {view === "saved" && (
          <>
            <h1 className="font-serif" style={{ fontSize: "2rem", marginBottom: "0.6rem" }}>Saved Properties</h1>
            <p className="body-text" style={{ marginBottom: "2rem" }}>Properties you've bookmarked for review.</p>
            {savedList.length === 0 ? (
              <div className="card" style={{ padding: "3rem", textAlign: "center" }}>
                <Bookmark size={32} color="var(--muted-2)" style={{ margin: "0 auto 1rem" }} />
                <p className="body-text">No saved properties yet. Browse deals and tap the star to save them.</p>
                <button className="btn-gold" style={{ marginTop: "1.4rem" }} onClick={() => setView("browse")}>Browse Deals</button>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: "1.2rem" }}>
                {savedList.map((d) => <DealCard key={d.id} d={d} />)}
              </div>
            )}
          </>
        )}

        {view === "calculator" && (
          <>
            <h1 className="font-serif" style={{ fontSize: "2rem", marginBottom: "1.6rem" }}>DSCR Calculator</h1>
            <MiniCalc />
          </>
        )}

        {view === "profile" && (
          <>
            <h1 className="font-serif" style={{ fontSize: "2rem", marginBottom: "1.6rem" }}>My Profile</h1>
            <div className="card" style={{ padding: "2.4rem", maxWidth: 620 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", marginBottom: "2rem" }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: "var(--gold-soft)", border: "1px solid var(--gold)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gold)", fontSize: "1.2rem" }}>{name.charAt(0)}{(user?.lastName || "R").charAt(0)}</div>
                <div>
                  <div className="font-serif" style={{ fontSize: "1.4rem" }}>{name} {user?.lastName}</div>
                  <div style={{ color: "var(--muted)", fontSize: "0.85rem" }}>{user?.role || "Real Estate Investor"}</div>
                </div>
              </div>
              <div style={{ display: "grid", gap: "0.9rem" }}>
                <input className="field" defaultValue={name} placeholder="First Name" />
                <input className="field" defaultValue={user?.email} placeholder="Email" />
                <input className="field" placeholder="Target Market(s)" />
                <select className="field"><option>Purchase Investment Property</option><option>Cash-Out / Equity Access</option><option>Scale Portfolio</option></select>
                <button className="btn-gold">Save Changes</button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
